import React, { useEffect, useRef, memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChartRenderer } from './ChartRenderer';
import { processMessageContent } from './chartUtils';
import './ChatbotModal.css';

// Custom link component for markdown links
const LinkComponent = ({ href, children }) => {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
};

// Tool Call Status Component
const ToolCallStatus = memo(({ toolCall }) => {
  return (
    <div className="chatbot-tool-call">
      <div className={`chatbot-tool-call-content chatbot-tool-call-${toolCall.status}`}>
        {toolCall.status === 'loading' && (
          <div className="chatbot-tool-call-indicator">
            <div className="chatbot-thinking-indicator">
              <span className="chatbot-thinking-dot"></span>
              <span className="chatbot-thinking-dot"></span>
              <span className="chatbot-thinking-dot"></span>
            </div>
            <span className="chatbot-tool-call-text">{toolCall.message}</span>
          </div>
        )}
        {toolCall.status === 'success' && (
          <div className="chatbot-tool-call-indicator">
            <span className="chatbot-tool-call-icon">✓</span>
            <span className="chatbot-tool-call-text">{toolCall.message}</span>
          </div>
        )}
        {toolCall.status === 'error' && (
          <div className="chatbot-tool-call-indicator">
            <span className="chatbot-tool-call-icon">✗</span>
            <span className="chatbot-tool-call-text">{toolCall.message}</span>
          </div>
        )}
      </div>
    </div>
  );
});

ToolCallStatus.displayName = 'ToolCallStatus';

// Memoize individual messages to prevent unnecessary re-renders
const Message = memo(({ message, onFollowupClick }) => {
  // Handle tool call messages
  if (message.role === 'tool') {
    return <ToolCallStatus toolCall={message} />;
  }

  // Process message content to extract charts
  const { text, charts, isGeneratingChart } = message.role === 'assistant' 
    ? processMessageContent(message.content)
    : { text: message.content, charts: [], isGeneratingChart: false };

  const followupQuestions = message.role === 'assistant' ? (message.followupQuestions || []) : [];

  return (
    <div
      className={`chatbot-message ${
        message.role === 'user' ? 'chatbot-message-user' : 'chatbot-message-assistant'
      } ${message.role === 'assistant' && charts.length > 0 ? 'chatbot-message-has-charts' : ''}`}
    >
      <div className="chatbot-message-content">
        {message.role === 'assistant' ? (
          <>
            {text && (
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]} 
                skipHtml={true}
                components={{ a: LinkComponent }}
              >
                {text}
              </ReactMarkdown>
            )}
            {charts.map((chartSpec, idx) => (
              <ChartRenderer key={idx} chartSpec={chartSpec} />
            ))}
            {isGeneratingChart && (
              <div className="chatbot-chart-generating">
                <div className="chatbot-chart-generating-icon">
                  <span className="chatbot-thinking-dot"></span>
                  <span className="chatbot-thinking-dot"></span>
                  <span className="chatbot-thinking-dot"></span>
                </div>
                <span>Generating visual…</span>
              </div>
            )}
            {followupQuestions.length > 0 && (
              <div className="chatbot-followup-questions">
                <p className="chatbot-followup-label">Follow-up:</p>
                <div className="chatbot-followup-buttons">
                  {followupQuestions.map((question, idx) => (
                    <button
                      key={idx}
                      className="chatbot-followup-button"
                      onClick={() => onFollowupClick && onFollowupClick(question)}
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          message.content
        )}
      </div>
    </div>
  );
});

Message.displayName = 'Message';

export const MessageList = ({ messages, isRunning, onSuggestionClick, suggestedPrompts = [] }) => {
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSuggestionClick = (promptText) => {
    if (onSuggestionClick) {
      onSuggestionClick(promptText);
    }
  };

  const handleFollowupClick = (question) => {
    if (onSuggestionClick) {
      onSuggestionClick(question);
    }
  };

  return (
    <div className="chatbot-messages">
      {messages.length === 0 && (
        <div className="chatbot-empty-state">
          <p className="chatbot-empty-state-greeting">👋 Hi! I'm your health assistant.</p>
          <p className="chatbot-empty-state-subtitle">Try asking me:</p>
          <div className="chatbot-suggestions">
            {suggestedPrompts.map((prompt) => (
              <button
                key={prompt.id}
                className="chatbot-suggestion-button"
                onClick={() => handleSuggestionClick(prompt.text)}
                disabled={isRunning}
                title={prompt.category ? `Category: ${prompt.category}` : ''}
              >
                {prompt.text}
              </button>
            ))}
          </div>
        </div>
      )}
      
      {messages.map((message) => (
        <Message key={message.id} message={message} onFollowupClick={handleFollowupClick} />
      ))}
      
      <div ref={messagesEndRef} />
    </div>
  );
};

